import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { flowMachine, flowReducer } from './flowMachine'
import {
  VerificationFlowContextType,
  VerificationFlowOptions,
  VerificationFlowState,
  VerificationEvent,
  PartnerType
} from './types'
import { TopicKey } from '../../../constants/customerMessages'

// Default options
const defaultOptions: VerificationFlowOptions = {
  enableVerificationFlow: false,
  initialPartnerType: 'LoanOfficer',
  membership: null,
  initialVerificationState: false,
  triggerOnMessageSent: true,
  verificationTriggerTopics: []
}

// Create context
const VerificationFlowContext = createContext<VerificationFlowContextType | undefined>(undefined)

interface VerificationFlowProviderProps {
  children: React.ReactNode
  options?: Partial<VerificationFlowOptions>
}

export const VerificationFlowProvider = ({ children, options: userOptions = {} }: VerificationFlowProviderProps) => {
  const options = { ...defaultOptions, ...userOptions }

  // Initialize state
  const initialState: VerificationFlowState = {
    currentStep: flowMachine.initial,
    partnerType: options.initialPartnerType,
    membership: options.membership,
    isVerified: options.membership?.isVerified || false
  }

  const [state, dispatch] = useReducer(flowReducer, initialState)

  // Update membership when it changes after initialization
  useEffect(() => {
    if (options.membership !== state.membership) {
      dispatch({
        type: 'UPDATE_MEMBERSHIP',
        payload: { membership: options.membership }
      })
    }
  }, [options.membership, state.membership])

  // Helper function to start verification with a specific partner type
  const startVerification = useCallback(
    (message: string, verifiedTopicKey: TopicKey, defaultTopicKey: TopicKey, partnerType?: PartnerType) => {
      if (!options.enableVerificationFlow || !options.membership) {
        dispatch({
          type: 'SEND_DEFAULT_MESSAGE',
          payload: {
            message,
            topicKey: defaultTopicKey,
            partnerType: partnerType || options.initialPartnerType
          }
        })
        return
      }

      dispatch({
        type: 'START_VERIFICATION',
        payload: {
          message,
          topicKey: verifiedTopicKey,
          partnerType: partnerType || options.initialPartnerType
        }
      })
    },
    [options.enableVerificationFlow, options.initialPartnerType]
  )

  return (
    <VerificationFlowContext.Provider value={{ state, dispatch, options, startVerification }}>
      {children}
    </VerificationFlowContext.Provider>
  )
}

// Custom hook to use the verification flow context
export const useVerificationFlow = (): VerificationFlowContextType => {
  const context = useContext(VerificationFlowContext)

  if (context === undefined) {
    throw new Error('useVerificationFlow must be used within a VerificationFlowProvider')
  }

  return context
}

// Helper function to check if a topic should trigger verification
export const shouldTriggerVerification = (topicKey: string, options: VerificationFlowOptions): boolean => {
  if (!options.enableVerificationFlow || !options.triggerOnMessageSent) {
    return false
  }

  if (!options.verificationTriggerTopics || options.verificationTriggerTopics.length === 0) {
    return true // If no specific topics are defined, trigger for all
  }

  return options.verificationTriggerTopics.includes(topicKey as any)
}
